import { UserContext } from "@/context/UserContext";
import { isAgentsDemoMode, useAgentsMode } from "@/libs/agentsMode";
import { ConfigParameters } from "@/types/api";

/**
 * Which parts of the new agent experience (`isNewAgentOnboarding`) have a real
 * backend yet.
 *
 * In demo mode (`isAgentsMock`) every feature is available and every method
 * below is served from the mock stores. In live mode a feature is available
 * only once `live` is true; until then it is hidden or disabled, so a real
 * tenant never sees mock data next to live data. See `@/libs/agentsMode`.
 *
 * Shipping an API, endpoint by endpoint:
 *  1. `yarn generate:specs` - the real method appears in `@/api`.
 *  2. Point the feature's hook at the generated service. Where the generated
 *     shape differs (all-optional fields, `{ body }`, enums), add a named
 *     adapter that takes the UI request and maps it for the live call only -
 *     see `AgentPolicyApi` (`src/pages/AgentEstate/policyApi.ts`). Its name
 *     must equal the generated method's (that is the mock and query key), so
 *     write it as an object shorthand method: the production minifier renames
 *     plain `function` declarations but keeps property keys.
 *  3. Register that name in `src/configs/mock.ts` (`[]`) and in `mockRegistry`,
 *     where the demo adapter keeps reading the UI request.
 *  4. Delete the temp method and its old mock keys, and update `methods` here.
 *     A new generated service or facade also goes in `useLogin`'s session-cache reset.
 *  5. Once every method of a feature is real, flip `live: true`. Real tenants
 *     get the feature on the next deploy; demo tenants see no change.
 *  6. Cover both modes in Cypress: demo mounts with `mockScope: true`, live
 *     mounts without it and intercepts the network with `MockApi`.
 */
export type AgentFeature =
  | "agentList"
  | "registryImport"
  | "platformOnboarding"
  | "agentDetails"
  | "policies"
  | "authoredPolicies"
  | "agentMission"
  | "agentAttributes"
  | "agentActivity"
  | "protectionDataSources"
  | "denyPolicyRules"
  | "multiRulePolicies"
  | "enforcementEnvironment"
  | "policyRecommendations"
  | "intentPolicyDraft"
  | "policyCatalogFilters"
  | "accessProtection"
  | "mcpAssociation"
  | "nativeProtectionPolicies"
  | "protectionVerification"
  | "protectionScenarios"
  | "dashboard"
  | "accessFlows"
  | "relationshipExplorer"
  | "guardian"
  | "oauthClientProfiles";

export interface AgentFeatureStatus {
  /** Every method below is served by the real backend. */
  live: boolean;
  /** The API methods the feature calls, by `method.name`. */
  methods: readonly string[];
}

export const AGENT_FEATURES: Record<AgentFeature, AgentFeatureStatus> = {
  // All Agents list and its Kill Switch - GET /api/v2/agents.
  agentList: {
    live: true,
    methods: ["listEstateAgentsUsingGet", "setAgentAccessRevocationUsingPost"],
  },
  // Import Agents → Microsoft Foundry, and the Agent Registries table.
  registryImport: {
    live: true,
    methods: [
      "listAgentRegistriesUsingGet",
      "validateUsingPost",
      "previewProjectsUsingPost",
      "importRegistryUsingPost",
      "syncAgentsUsingPost",
      "deleteRegistryUsingDelete",
      "syncAttributesUsingPost",
    ],
  },
  // Import Agents → every other platform, and the discovery/sync-job run.
  platformOnboarding: {
    live: false,
    methods: [
      "listAgentPlatformCatalogUsingGet",
      "listAgentEnvironmentsUsingGet",
      "testAgentEnvironmentConnectionUsingPost",
      "testAgentIdentityConnectionUsingPost",
      "startAgentOnboardingUsingPost",
      "disconnectAgentEnvironmentUsingDelete",
      "getAgentOnboardingProgressUsingGet",
      "startAgentDiscoveryUsingPost",
      "getAgentDiscoveryUsingGet",
      "listDiscoveredProjectAgentsUsingGet",
    ],
  },
  agentDetails: {
    live: true,
    methods: [
      "getEstateAgentUsingGet",
      "resolveAgentPolicyCoverageUsingPost",
      "getEffectiveEnforcementUsingGet",
    ],
  },
  // Policy review, apply and the native-policy implementations.
  policies: {
    live: true,
    methods: [
      "resolveAgentPolicyRecommendationsUsingPost",
      "resolvePolicyCatalogUsingPost",
      "getPolicyReviewUsingPost",
      "previewAgentPoliciesUsingPost",
      "applyPoliciesUsingPost",
      "listPolicyImplementationsUsingGet",
      "updateNativePolicyUsingPut",
      "reapplyPolicyDefinitionUsingPost",
      "retryPolicyDeploymentUsingPost",
    ],
  },
  // Agent policies authored with the rule builder (Create Agent Policy →
  // Build with Conditions) and tool customizations on Agent Details →
  // Resources. Live sends the demo's shape (TRUST-13500 comment 58796), which
  // the backend is still moving to - see `AuthoredPolicyApi`.
  authoredPolicies: {
    live: true,
    methods: [
      "listAgentPoliciesUsingGet",
      "createAgentPolicyUsingPost",
      "updateAgentPolicyUsingPut",
      "deleteAgentPolicyUsingDelete",
    ],
  },
  // Agent Details -> Access & Protection (ex Resources + Policy Coverage).
  // Live projects the record from agent details (`useAgentProtection`); tool
  // rules ride on `authoredPolicies`. The method is the demo store's read.
  accessProtection: {
    live: true,
    methods: ["getAgentProtectionUsingGet"],
  },
  // The parts below have no backend yet. Open questions:
  //  - Association API: one contract for MCP services and data sources? Batch
  //    with per-item results? Can a discovered relationship be removed, or
  //    only hidden until the next registry sync?
  //  - Policy applicability / effective access: which service evaluates the
  //    agent's expected controls against deployed policies, and what is its
  //    "stale" signal after an association or policy change?
  //  - Native principal mapping: where is the agent -> native role mapping
  //    stored, and how is it verified against the platform?
  //  - Native policy mutation and deployment: reuse the data-policy endpoints
  //    (createAccessPolicyUsingPost / publishPolicyUsingPost) with an agent
  //    context field, and a per-operation result with retry of failed ops?
  //  - Rule ownership and applicability: an endpoint listing every MCP rule
  //    that applies to (agent, server[, tool]) with its owning policy, that
  //    policy's ownership (agent-specific / agent / shared) and where it is
  //    managed, and each rule's deployment status.
  //  - Agent-specific rules (tool- or server-scoped): rule-level endpoints on
  //    an agent-specific MCP policy that never write to a shared one, or a
  //    whole-policy replace with a version?
  //  - Native policy deployment status per policy, readable without a run.
  //  - Authoritative enforcement verification: what evidence marks a gateway
  //    path verified, and what confirms a bypass?
  //  - Reconciliation on change (registry sync, policy drift).
  // Add / Remove MCP services: live on the existing MCP Registry and
  // mcp_server_bindings APIs (`AgentProtectionApi`).
  mcpAssociation: {
    live: true,
    methods: [
      "listProtectionCandidatesUsingGet",
      "associateAgentResourcesUsingPost",
      "removeAgentResourceAssociationUsingDelete",
    ],
  },
  // Native data-source policies: the wizard, deployment and its retry.
  nativeProtectionPolicies: {
    live: false,
    methods: [
      "getDataSourceCatalogUsingGet",
      "saveNativePolicyDraftUsingPost",
      "verifyNativeRoleMappingUsingPost",
      "deployProtectionChangesUsingPost",
      "retryFailedProtectionOperationsUsingPost",
    ],
  },
  // Reassess, and the gateway checks on an enforcement path.
  protectionVerification: {
    live: false,
    methods: [
      "reassessAgentProtectionUsingPost",
      "simulateGatewayCheckUsingPost",
    ],
  },
  // The development-only scenario menu; never a backend.
  protectionScenarios: {
    live: false,
    methods: [
      "setProtectionScenarioUsingPut",
      "resetAgentProtectionDemoUsingPost",
    ],
  },
  // Demo-only sections of live pages, agreed for this sprint (TRUST-13500
  // comment 58797). Where `methods` is empty, only the section is hidden.
  // Agent Details → Overview mission card and its panels.
  agentMission: {
    live: false,
    methods: ["updateAgentMissionUsingPost"],
  },
  // Agent Details → Agent Attributes from the demo store. Live reads the
  // entitlement API (user_entitlement_attribute/value) instead.
  agentAttributes: {
    live: false,
    methods: [
      "listEstateAgentAttributesUsingGet",
      "updateEstateAgentAttributesUsingPatch",
    ],
  },
  // Agent Details → Activity from the agent's recent activity. Live shows its
  // Policy Evaluation History instead.
  agentActivity: {
    live: false,
    methods: [],
  },
  // Access & Protection's Data Sources view, Add Data Sources and Edit
  // Association: no backend yet.
  protectionDataSources: {
    live: false,
    methods: ["updateAgentResourceAssociationUsingPut"],
  },
  // "Deny when conditions match" on a new agent policy's rules.
  denyPolicyRules: {
    live: false,
    methods: [],
  },
  // More than one rule per policy: Add Rule and Duplicate.
  multiRulePolicies: {
    live: false,
    methods: [],
  },
  // The demo enforcement environment. Live reads the tenant's saved setup.
  enforcementEnvironment: {
    live: false,
    methods: ["getEnforcementEnvironmentUsingGet"],
  },
  // Review Policies → Recommended tab.
  policyRecommendations: {
    live: false,
    methods: [],
  },
  // Review Policies → Create New → Generate from Intent.
  intentPolicyDraft: {
    live: false,
    methods: ["generateDraftPolicyUsingPost"],
  },
  // Review Policies → All Policies category chips and technology filter.
  policyCatalogFilters: {
    live: false,
    methods: [],
  },
  // The TrustAI Executive Board on /app/dashboard. The board itself and its
  // estate cards (getInventorySummaryUsingGet, live in both modes, so not
  // listed) render everywhere; the sections reading the methods below render
  // in demo mode only, until their live API is complete (TRUST-13500).
  dashboard: {
    live: false,
    methods: [
      "getAgentEstateSummaryUsingGet",
      "getEnterprisePostureUsingGet",
      "listPriorityControlsUsingGet",
      "getEstateReadinessUsingGet",
      "startOnboardingRunUsingPost",
      "startEvaluationUsingPost",
      "getControlDecisionUsingGet",
      "listAgentRegistryUsingGet",
      "getVerifiedOutcomesUsingGet",
      "listAuditEvidenceUsingGet",
      "enforcePolicyUsingPost",
      "listRelevantIntelligenceUsingGet",
      "getEstateInventoryUsingGet",
      "getWhatsChangedUsingGet",
      "getDataGovernanceOverviewUsingGet",
      "listDataPlatformGovernanceUsingGet",
      "getDataPlatformGovernanceUsingGet",
      "getDataGovernanceSummaryUsingGet",
      "getAgentFleetProtectionUsingGet",
      "getAgentActivitySummaryUsingGet",
    ],
  },
  accessFlows: {
    live: false,
    methods: [
      "getAgentFlowSummaryUsingGet",
      "listAgentAccessFlowsUsingGet",
      "getAgentAccessFlowUsingGet",
    ],
  },
  relationshipExplorer: {
    live: false,
    methods: ["getRelationshipGraphUsingGet"],
  },
  guardian: {
    live: false,
    methods: [
      "getGuardianContextUsingGet",
      "askGuardianUsingPost",
      "listGuardianPromptsUsingGet",
      "listGuardianRecommendationsUsingGet",
    ],
  },
  oauthClientProfiles: {
    live: false,
    methods: [
      "listOAuthClientProfilesUsingGet",
      "getOAuthClientProfileUsingGet",
      "getOAuthClientProfileFormOptionsUsingGet",
      "listOAuthAuthorizationProvidersUsingGet",
      "getOAuthAuthorizationProviderUsingGet",
      "createOAuthClientProfileUsingPost",
      "updateOAuthClientProfileUsingPatch",
      "validateOAuthClientProfileUsingPost",
      "getOAuthClientProfileValidationUsingGet",
      "cancelOAuthClientProfileValidationUsingPost",
      "getOAuthClientProfileUsageUsingGet",
      "previewOAuthClientProfileImpactUsingPost",
      "archiveOAuthClientProfileUsingPost",
      "restoreOAuthClientProfileUsingPost",
      "listOAuthClientProfileOptionsUsingPost",
    ],
  },
};

/**
 * Non-hook form, for route helpers, loaders and menu filters. Pass the
 * component's `useConfigParameters()` where there is one.
 */
export const isAgentFeatureAvailable = (
  feature: AgentFeature,
  params: ConfigParameters = UserContext.getConfigParameters(),
): boolean => isAgentsDemoMode(params) || AGENT_FEATURES[feature].live;

/**
 * Whether `/app/dashboard` is the TrustAI Executive Board rather than the
 * legacy Security Overview. The route swap, the default landing path and the
 * app shell all read this, so they agree on which page is showing.
 *
 * The flag alone decides it: in live mode the board shows its estate cards
 * from the real inventory summary and withholds its mock-only sections.
 */
export const isAgentBoardEnabled = (
  params: ConfigParameters = UserContext.getConfigParameters(),
): boolean => !!params.isNewAgentOnboarding;

/**
 * Hook form. Reads the mode from `<MockScope>` (see `useAgentsMode`), so it
 * agrees with what the surrounding queries actually resolve against.
 */
export const useAgentFeature = (feature: AgentFeature) => {
  const mode = useAgentsMode();
  return {
    mode,
    available: mode === "demo" || AGENT_FEATURES[feature].live,
  };
};

/**
 * Guardian, the agent assistant, replaces the legacy TrustAI Copilot - but
 * its runtime is mock-only until its API ships, so a live tenant keeps the
 * Copilot until then.
 */
export const isGuardianEnabled = (
  params: ConfigParameters = UserContext.getConfigParameters(),
): boolean =>
  !!params.isNewAgentOnboarding && isAgentFeatureAvailable("guardian", params);
